import { useState, useEffect, useReducer } from 'react'
import { wf, saveViewState } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { loadLevels, loadUnassignedRooms } from '../outline/design.js'
import { getCriteria, getConcepts, getSpecifications, getOrganizations, updateConceptLevel } from '../services/design.js'
import { getParcels } from '../services/parcels.js'
import { getConceptSpecSections } from '../services/specSections.js'
import { getProjectBimSystems } from '../services/bim.js'
import { BimRow, BimSystemZoom, BimElementZoom, BimPhaseZoom } from './BimSystemList.jsx'
import { ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider, useActionMenu } from './ActionMenu.jsx'
import { ParcelDetailContent } from './ParcelDetailSection.jsx'
import { ConceptLevelAreaMetrics } from './ConceptLevelAreaFields.jsx'
import ConceptRoomsSection, { RoomDetailZoom } from './ConceptRoomsSection.jsx'
import ConceptUnitsSection from './ConceptUnitsSection.jsx'
import ConceptDevMetricsSection from './ConceptDevMetricsSection.jsx'
import ConceptFieldCategorySection, { CONCEPT_CATEGORIES } from './ConceptFieldCategorySection.jsx'
import ControlBudgetSection from './ControlBudgetSection.jsx'
import ConceptLevelsSection from './ConceptLevelsSection.jsx'
import { ONBOARDING_QUESTIONNAIRE } from '../journey/onboardingQuestionnaire.js'
import { InspirationRows, useInspirationSummary, openTeamInspirationBoard } from './inspiration/InspirationOutline.jsx'

/**
 * Persist an area-field edit for a Concept Level and refresh the shared
 * ctx.levelCache entry in place so the level-detail zoom (the one remaining
 * caller — the tree/other zooms now go through ConceptLevelsSection.jsx) reflects
 * the change without a refetch.
 */
async function saveLevelAreaFields(unitId, levelId, fields) {
  await updateConceptLevel(levelId, fields)
  const arr = ctx.levelCache?.[unitId]
  if (Array.isArray(arr)) {
    const idx = arr.findIndex(l => l.id === levelId)
    if (idx !== -1) {
      const m = { ...arr[idx].fields, ...fields }
      arr[idx] = {
        ...arr[idx],
        fields: {
          ...m,
          'Total Living Area':             (m['New Living Area'] || 0) + (m['Existing Living Area'] || 0),
          'Total Garage Area':             (m['New Garage Area'] || 0) + (m['Existing Garage Area'] || 0),
          'Total Elevated Decks Area':     (m['New Elevated Decks Area'] || 0) + (m['Existing Elevated Decks Area'] || 0),
          'Total Mechanical Storage Area': (m['New Mechanical Storage Area'] || 0) + (m['Existing Mechanical Storage Area'] || 0),
        },
      }
    }
  }
  ctx.render()
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function fmtDate(d) {
  if (!d) return ''
  const p = d.split('-')
  return MONTHS[parseInt(p[1]) - 1] + ' ' + parseInt(p[2])
}

function Section({ label, type, count, collapsed, onToggle, children, zoomKey, menuItems }) {
  const menu = useActionMenu()
  // zoomKey (e.g. 'sec:design:parcels') makes the bullet zoom INTO this section; otherwise it
  // focuses the Design root (the default for sections React doesn't own a dedicated zoom for).
  const focus = () => {
    if (zoomKey && ctx.zoomToSection) ctx.zoomToSection(zoomKey, { root: 'design' })
    else if (ctx.zoomToRoot) ctx.zoomToRoot('design')
  }
  const key = `sec:design:${type}`
  return (
    <div className={`wf-node wf-node--section wf-node--section-${type}${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={onToggle}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button
          className="wf-bullet wf-bullet--section"
          title={`Focus on ${label}`}
          data-kbzoom="1"
          onClick={focus}
        ></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">{label}</span>
          {count != null && (
            <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: '8px' }}>{count}</span>
          )}
        </div>
        {menuItems ? <ActionMenuButton menu={menu} /> : (
          <button
            className="wf-menu-btn"
            data-fn="openMenu"
            data-type="section"
            data-key={`sec:design:${type}`}
            data-sectiontype={type}
          >&#8943;</button>
        )}
      </div>
      {menuItems && <ActionMenu menu={menu}>{menuItems(menu)}</ActionMenu>}
      {!collapsed && <div className="wf-children">{children}</div>}
    </div>
  )
}

// Owner Criteria answer helpers (the record stores one value per questionnaire field key).
// Shared by DesignSection's own Criteria branch and MilestoneSections' CriteriaSection so both
// contexts render the identical expandable category tree (per CLAUDE.md's One-Component-Per-
// Node-Type rule) — the previous Reference > Design > Criteria branch rendered a flat, non-
// expanding list of raw records instead of this categorized questionnaire view.
export function crHasVal(f, key) { const v = f && f[key]; return Array.isArray(v) ? v.length > 0 : !!(v != null && String(v).trim()) }
export function crFmt(v) { return Array.isArray(v) ? v.join(', ') : (v == null ? '' : String(v)) }

/** Total answered questions across every questionnaire category, for a section header count. */
export function criteriaAnsweredTotal(record) {
  const f = record ? (record.fields || {}) : null
  if (!f) return null
  return ONBOARDING_QUESTIONNAIRE.steps.reduce((n, s) => n + s.questions.filter(q => crHasVal(f, q.key)).length, 0)
}

/**
 * The Owner Criteria questionnaire, grouped into expandable category branches (one per
 * ONBOARDING_QUESTIONNAIRE step) with each question as a leaf row. `ownerKey` scopes the
 * collapse-state keys so the milestone-mounted and Reference-mounted instances don't fight
 * over the same wf.collapsed entries.
 */
export function CriteriaCategoryRows({ record, ownerKey }) {
  const f = record ? (record.fields || {}) : null
  if (!f) return null
  return (
    <>
      {ONBOARDING_QUESTIONNAIRE.steps.map(step => {
        const ckey = 'crcat:' + ownerKey + ':' + step.key
        const catCollapsed = wf.collapsed[ckey] !== false
        const answered = step.questions.filter(q => crHasVal(f, q.key)).length
        return (
          <div key={step.key} className={`wf-node wf-node--section${catCollapsed ? ' wf-node--collapsed' : ''}`} data-key={ckey}>
            <div className="wf-row">
              <button className="wf-triangle" data-fn="toggleCollapse" data-key={ckey}>
                <span className={`wf-tri-icon ${catCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
              </button>
              <span className="wf-bullet wf-bullet--section"></span>
              <div className="wf-text-wrap">
                <span className="wf-node-text wf-node-text--section">{step.title}</span>
                <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{answered}/{step.questions.length}</span>
              </div>
            </div>
            {!catCollapsed && (
              <div className="wf-children">
                {step.questions.map(q => {
                  const has = crHasVal(f, q.key)
                  const note = f[q.key + '_notes']
                  return (
                    <div key={q.key} className="wf-node wf-node--item">
                      <div className="wf-row" style={{ alignItems: 'flex-start' }}>
                        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                        <span className="wf-bullet wf-bullet--item" style={{ marginTop: 6 }}></span>
                        <div className="wf-text-wrap" style={{ flexWrap: 'wrap' }}>
                          <span className="wf-node-text" style={{ flex: '1 1 0', minWidth: 0, whiteSpace: 'normal' }}>
                            <span style={{ color: 'var(--aj-text-mid)' }}>{q.label}</span>
                            <span style={{ display: 'block', marginTop: 2, color: has ? 'var(--aj-text)' : 'var(--aj-text-muted)', fontStyle: has ? 'normal' : 'italic', fontWeight: has ? 600 : 400 }}>
                              {has ? crFmt(f[q.key]) : 'Not answered'}
                            </span>
                            {note && String(note).trim() && (
                              <span style={{ display: 'block', marginTop: 2, color: 'var(--aj-text-muted)', fontSize: 12 }}>Note: {String(note)}</span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </>
  )
}

/**
 * Canonical Parcel row — expand triangle (reveals the research branch inline) + zoomable bullet
 * (into the parcel-detail view) + ⋯ menu (Open Research Tool / Generate Pre-Design Report /
 * Duplicate Parcel). One component per node type (CLAUDE.md): used in both the project-home and
 * design-root-zoom render paths. The inline branch reuses ParcelDetailContent — the same content
 * the zoom renders — so expand and zoom show an identical tree.
 */
export function ParcelRow({ rec, projectId }) {
  const menu = useActionMenu()
  const f = rec.fields
  const name = f['APN'] || f['Name'] || f['Parcel Name'] || rec.id
  const zone = f['Zone String'] || (Array.isArray(f['Zone']) ? null : f['Zone'])
  const key = `parcel-detail:${rec.id}`
  const collapsed = wf.collapsed[key] !== false
  function toggle() {
    wf.collapsed[key] = (wf.collapsed[key] === false)   // collapsed default; flip
    saveViewState()
    window.dispatchEvent(new CustomEvent('outline-render'))
  }
  return (
    <div className={`wf-node wf-node--item${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle} title={collapsed ? 'Expand' : 'Collapse'}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button
          className="wf-bullet wf-bullet--item"
          title="Zoom into parcel"
          onClick={() => ctx.zoomTo && ctx.zoomTo('parcel-detail', { parcelId: rec.id })}
        ></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text">{name}</span>
          {zone && <span className="wf-status-pill">{zone}</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-parcel-research-tool', { detail: { parcelId: rec.id } }))}>
          Open Research Tool
        </ActionMenuItem>
        <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('generate-predesign-report', { detail: { projectId } }))}>
          Generate Pre-Design Report
        </ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-duplicate-parcel', { detail: { parcelId: rec.id } }))}>
          Duplicate Parcel
        </ActionMenuItem>
      </ActionMenu>
      {!collapsed && <div className="wf-children"><ParcelDetailContent parcelId={rec.id} /></div>}
    </div>
  )
}

/**
 * Canonical Concept row — expand triangle (reveals Units inline via ConceptUnitsSection,
 * same content the concept-detail zoom shows) + zoomable bullet + ⋯ menu (Open presentation /
 * Set as active / Edit name / Delete). Used by every conceptsContent render path (design root
 * zoom, home, referenceZoom, conceptsZoom) AND by the Concepts branch mounted under
 * CONCEPT_BRANCH_MS_TYPES milestones (MilestoneSections.jsx) — exported so both stay one
 * component per CLAUDE.md's node-type rule. The ⋯ menu is DOM-attribute driven (data-fn="openMenu"
 * data-type="concept") and wired by the outline engine's global delegated listener, so it works
 * unmodified regardless of which React tree rendered the row.
 *
 * "Active" is UI terminology only (Round 8, 2026-07) — the underlying Airtable field is still
 * named `Selected Concept` (data layer unchanged; only what the user reads changed, since this
 * concept isn't just "picked," it's the one actively driving Specifications, Schedules, and
 * every other project surface that reads off "the concept").
 */
export function ConceptRow({ rec, recordId, hideAreaMetrics }) {
  const f = rec.fields
  const name = f['Parti Pris Title'] || 'Unnamed Concept'
  const active = f['Selected Concept'] === true
  const key = 'concept-detail:' + rec.id
  const collapsed = wf.collapsed[key] !== false
  return (
    <div className={`wf-node wf-node--concept${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={key}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button
          className="wf-bullet wf-bullet--concept"
          title="Zoom into concept"
          onClick={() => ctx.zoomTo && ctx.zoomTo('concept-detail', { conceptId: rec.id })}
        ></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--concept">{name}</span>
          {active && (
            <span className="wf-status-pill" style={{ color: '#1D9E75', borderColor: '#1D9E7533' }}>Active</span>
          )}
        </div>
        <button
          className="wf-menu-btn"
          data-fn="openMenu"
          data-type="concept"
          data-key={`concept-menu:${rec.id}`}
          data-conceptid={rec.id}
          data-owner={recordId}
        >&#8943;</button>
      </div>
      {!collapsed && (
        <div className="wf-children">
          <ControlBudgetSection conceptId={rec.id} />
          <ConceptUnitsSection conceptId={rec.id} hideAreaMetrics={hideAreaMetrics} />
          <ConceptDevMetricsSection conceptId={rec.id} />
          <ConceptFieldCategorySection conceptId={rec.id} suffix="demolition" />
          <ConceptFieldCategorySection conceptId={rec.id} suffix="grading" />
          <ConceptFieldCategorySection conceptId={rec.id} suffix="pools" />
          <ConceptFieldCategorySection conceptId={rec.id} suffix="sitescope" />
        </div>
      )}
    </div>
  )
}

/* ── ConceptCandidatesBranch — mirrors the Team section's subconsultant Candidates node ───
   Collapsible, expand/collapse only (no zoom); ALWAYS renders, even with zero candidates
   (Round 8) — this is where "Add concept" now lives (moved off the Concepts section's own
   menu), so the affordance to add the project's first concept has to be visible even before
   one exists. `ownerKey` namespaces the collapse key under whichever Concepts section owns
   this list (sec:design:concepts, or sec:<msId>:concepts). */
function ConceptCandidatesBranch({ candidates, recordId, hideAreaMetrics, ownerKey, readOnly, hasActive }) {
  const menu = useActionMenu()
  const key = ownerKey + ':candidates'
  const collapsed = wf.collapsed[key] !== false
  function toggle(e) {
    e.stopPropagation()
    wf.collapsed[key] = !collapsed
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }
  return (
    <div className={`wf-node wf-node--section${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <span className="wf-bullet wf-bullet--section"></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Candidates</span>
          <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{candidates.length}</span>
        </div>
        {!readOnly && <ActionMenuButton menu={menu} />}
      </div>
      {!readOnly && (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={() => window.dispatchEvent(new CustomEvent('open-add-concept-modal', { detail: { projectId: recordId } }))}>
            Add concept
          </ActionMenuItem>
        </ActionMenu>
      )}
      {!collapsed && (
        <div className="wf-children">
          {candidates.length === 0 && (
            <div className="wf-loading" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>
              {hasActive
                ? (readOnly ? 'No other candidates proposed.' : 'No other candidates proposed — use ⋯ Add concept to propose an alternative.')
                : (readOnly ? 'No concepts yet.' : 'No concepts yet — use ⋯ Add concept.')}
            </div>
          )}
          {candidates.map(c => <ConceptRow key={c.id} rec={c} recordId={recordId} hideAreaMetrics={hideAreaMetrics} />)}
        </div>
      )}
    </div>
  )
}

/**
 * ConceptsList — the canonical rendering of a project's concept list, everywhere Concepts
 * mounts (Reference > Design > Concepts and the ICP/CF/IDP milestones). Mirrors the Team
 * section's subconsultant Candidates/Awarded pattern: the concept the project has settled on
 * (the ACTIVE concept — Selected Concept = true underneath) renders directly, prominently, as
 * a sibling — every other concept still under consideration lives inside a collapsible
 * "Candidates" sub-node below it. The Candidates node always renders (Round 8), even with zero
 * concepts total, since that's now the only place a new concept gets added.
 */
export function ConceptsList({ concepts, recordId, hideAreaMetrics, ownerKey, readOnly = false }) {
  if (!concepts) return <div className="wf-loading">Loading concepts...</div>
  const active = concepts.find(c => c.fields['Selected Concept'] === true) || null
  const candidates = concepts.filter(c => c !== active)
  return (
    <>
      {active && <ConceptRow key={active.id} rec={active} recordId={recordId} hideAreaMetrics={hideAreaMetrics} />}
      <ConceptCandidatesBranch candidates={candidates} recordId={recordId} hideAreaMetrics={hideAreaMetrics} ownerKey={ownerKey} readOnly={readOnly} hasActive={!!active} />
    </>
  )
}

/* ── Room node (leaf) ───────────────────────────────────────────────────── */
function RoomRow({ room, levelId }) {
  const f    = room.fields
  const name = f['Room'] || f['Room Number and Name'] || 'Room'
  const rkey = 'room:' + room.id
  return (
    <div className="wf-node wf-node--item" data-key={rkey}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <button className="wf-bullet wf-bullet--item" data-fn="openMenu"
          data-type="room" data-roomid={room.id} data-levelid={levelId}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text">{name}</span>
        </div>
        <button className="wf-menu-btn" data-fn="openMenu" data-type="room"
          data-roomid={room.id} data-levelid={levelId}>&#8943;</button>
      </div>
    </div>
  )
}

/* ── Unassigned rooms section ───────────────────────────────────────────── */
function UnassignedSection({ conceptId, rooms }) {
  const key = 'sec:concept-unassigned:' + conceptId
  const [collapsed, setCollapsed] = useState(() => wf.collapsed[key] !== false)
  function toggle() {
    setCollapsed(c => { const n = !c; wf.collapsed[key] = n; saveViewState(); return n })
  }
  return (
    <div className={`wf-node wf-node--section wf-node--section-unassigned${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <span className="wf-bullet wf-bullet--section"></span>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">Unassigned Rooms ({rooms.length})</span>
        </div>
      </div>
      {!collapsed && (
        <div className="wf-children">
          {rooms.map(room => <RoomRow key={room.id} room={room} levelId="" />)}
        </div>
      )}
    </div>
  )
}

function calcDesignVis() {
  if (!wf.zoomProjectId) return 'hidden'
  if (wf.searchQuery) return 'hidden'
  // Concept hierarchy focus zooms (WorkFlowy model: focused node becomes root)
  const zs = wf.zoomSection || ''
  if (zs.startsWith('concept-detail:')) return 'conceptZoom'
  if (zs.startsWith('unit-detail:'))    return 'unitZoom'
  if (zs.startsWith('level-detail:'))   return 'levelZoom'
  if (zs.startsWith('room-detail:'))    return 'roomZoom'
  // Category-wrapper zooms (Units/Metrics/Demolition/Grading/Pools/Site Scope/Levels/Rooms) —
  // children-only, ZoomTitle supplies the header. Suffix after 'sec:concept:<id>:' selects which.
  if (zs.startsWith('sec:concept:')) {
    const _suffix = zs.split(':')[3] || 'units'
    if (_suffix === 'metrics') return 'conceptMetricsZoom'
    if (CONCEPT_CATEGORIES[_suffix]) return 'conceptCategoryZoom'
    return 'conceptUnitsZoom'
  }
  if (zs.startsWith('sec:unit:'))       return 'unitLevelsZoom'
  if (zs.startsWith('sec:level:'))      return 'levelRoomsZoom'
  // BIM System / Element entity zooms (bullet-zoom from a BimRow/BimElementRow) — children-only.
  if (zs.startsWith('bim-system-detail:') || zs.startsWith('bim-element-detail:') || zs.startsWith('bim-phase-detail:')) return 'bimEntityZoom'
  // Every Design section is a React-owned children-only zoom. Unknown sec:design:* keys
  // (e.g. the stale 'specs', now under Production) fall back to the Design root view.
  if (zs.startsWith('sec:design:')) {
    return { 'sec:design:criteria': 'criteriaZoom', 'sec:design:parcels': 'parcelsZoom',
      'sec:design:concepts': 'conceptsZoom', 'sec:design:bim': 'bimZoom', 'sec:design:inspiration': 'inspirationZoom' }[zs] || 'designZoom'
  }
  // Design root zoom: focused on Design, no section/concept drilldown active
  if (wf.zoomRoot === 'design' && !wf.zoomPhaseId && !wf.zoomMsId && !wf.zoomSection &&
      !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId) return 'designZoom'
  // Reference zoom: root row renders as a child of the Reference collection
  if (wf.zoomRoot === 'reference' && !wf.zoomPhaseId && !wf.zoomMsId && !wf.zoomSection &&
      !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId) return 'referenceZoom'
  // Home: no zoom active at all
  if (!wf.zoomRoot && !wf.zoomPhaseId && !wf.zoomMsId && !wf.zoomSection &&
      !wf.zoomCat && !wf.zoomItemId && !wf.zoomConceptId) return 'home'
  return 'hidden'
}

export default function DesignSection() {
  const [, forceUpdate] = useReducer(v => v + 1, 0)
  // Collapse state is read LIVE from wf.collapsed every render (never local useState) so external
  // changes — the Action Menu Expand all / Collapse all buttons and Shift+Space, which write
  // wf.collapsed via expandCollapseDescendants — take effect here. (Per the CLAUDE.md collapse rule.)
  const rootCollapsed = wf.collapsed['root:design'] !== false
  const sec = {
    criteria: wf.collapsed['sec:design:criteria'] !== false,
    parcels:  wf.collapsed['sec:design:parcels']  !== false,
    concepts: wf.collapsed['sec:design:concepts'] !== false,
    bim:      wf.collapsed['sec:design:bim']      !== false,
    specs:    wf.collapsed['sec:design:specs']    !== false,
    inspiration: wf.collapsed['sec:design:inspiration'] !== false,
  }
  const [criteria, setCriteria] = useState(null)
  const [parcels, setParcels] = useState(null)
  const [concepts, setConcepts] = useState(null)
  const [bim, setBim] = useState(null)
  const [specs, setSpecs] = useState(null)
  // Supabase spec sections for the approved concept (Selected Concept = true).
  // undefined = not started, null = loading, [] / [...] = loaded.
  const [specSections, setSpecSections] = useState(undefined)
  const [approvedConceptId, setApprovedConceptId] = useState(null)  // Airtable record id
  const [recordId, setRecordId] = useState(() => wf.zoomProjectId || null)
  const [vis, setVis] = useState(() => calcDesignVis())

  const SEC_WF_KEYS = {
    criteria: 'sec:design:criteria',
    parcels:  'sec:design:parcels',
    concepts: 'sec:design:concepts',
    bim:      'sec:design:bim',
    specs:    'sec:design:specs',
    inspiration: 'sec:design:inspiration',
  }

  function toggleSec(key) {
    const wfk = SEC_WF_KEYS[key]
    wf.collapsed[wfk] = !(wf.collapsed[wfk] !== false)  // toggle collapsed state
    saveViewState()
    forceUpdate()
  }

  /* Re-resolve concepts + the approved concept + its spec sections. Called on
     project change AND when the outline fires `concepts-updated` (e.g. after
     "Set as selected"), so the spec gate refreshes without a page reload. */
  function loadConceptsAndSpecs(pid) {
    getConcepts(pid).then(cs => {
      setConcepts(cs)
      const approved = (cs || []).find(c => c.fields['Selected Concept'] === true)
      const cid = approved ? approved.id : null
      setApprovedConceptId(cid)
      setSpecSections(null)
      if (cid) {
        // NOTE: keep this in LOCAL state only. `ctx.specSectionCache` is owned by the Reference >
        // Specifications branch and holds the version-bearing shape (getSpecSectionsWithVersions).
        // getConceptSpecSections returns sections WITHOUT a `versions` field — writing it here used to
        // poison that cache so every reference section read as "⚠ Not assigned to a milestone".
        getConceptSpecSections(cid)
          .then(s => { setSpecSections(s) })
          .catch(() => { setSpecSections([]) })
      } else {
        setSpecSections([])
      }
    }).catch(() => { setConcepts([]); setApprovedConceptId(null); setSpecSections([]) })
  }

  // Refresh concepts/spec-gate when the outline signals a concept change
  useEffect(() => {
    function onConceptsUpdated() {
      const pid = ctx.recordId || wf.zoomProjectId
      if (pid) loadConceptsAndSpecs(pid)
    }
    window.addEventListener('concepts-updated', onConceptsUpdated)
    return () => window.removeEventListener('concepts-updated', onConceptsUpdated)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh parcels when a parcel is added from elsewhere (e.g. the Pre-design Research
  // milestone's own Parcels branch) — mirrors the concepts-updated listener above.
  useEffect(() => {
    function onParcelsUpdated() {
      const pid = ctx.recordId || wf.zoomProjectId
      if (pid) getParcels(pid).then(recs => { ctx.parcelsCache = recs; setParcels(recs) }).catch(() => {})
    }
    window.addEventListener('parcels-updated', onParcelsUpdated)
    return () => window.removeEventListener('parcels-updated', onParcelsUpdated)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!recordId) return
    /* Reset data on project change */
    setCriteria(null); setParcels(null); setConcepts(null); setBim(null); setSpecs(null)
    setSpecSections(undefined); setApprovedConceptId(null)

    getCriteria(recordId).then(setCriteria).catch(() => setCriteria([]))
    getParcels(recordId).then(recs => { ctx.parcelsCache = recs; setParcels(recs) }).catch(() => setParcels([]))
    loadConceptsAndSpecs(recordId)
    getProjectBimSystems(recordId)
      .then(records => {
        setBim(records)
        // Mirror into ctx.bimCache — shared with MilestoneSections.jsx's own independent load,
        // and read by outline.js's breadcrumb/ZoomTitle resolution for the entity zooms below.
        ctx.bimCache = records
        window.dispatchEvent(new Event('outline-render'))
      })
      .catch(() => setBim([]))
    getSpecifications(recordId).then(setSpecs).catch(() => setSpecs([]))
    // Load orgs — shared cache read by Team/contract/agency-review pickers elsewhere.
    if (!ctx.orgsCache) {
      ctx.orgsCache = []
      getOrganizations().then(list => { ctx.orgsCache = list }).catch(() => { ctx.orgsCache = [] })
    }
  }, [recordId])

  useEffect(() => {
    // SetUpBimSystemsModal already wrote the new rows into ctx.bimCache — mirror that into this
    // component's own `bim` state so the BimRow list re-renders without a refetch/reload.
    function onBimAdded(e) {
      const added = e.detail?.systems || []
      if (!added.length) return
      setBim(prev => [...(prev || []), ...added])
    }
    window.addEventListener('bim-systems-added', onBimAdded)
    return () => window.removeEventListener('bim-systems-added', onBimAdded)
  }, [])

  useEffect(() => {
    function onRender() {
      const newRecordId = wf.zoomProjectId || null
      setRecordId(prev => prev !== newRecordId ? newRecordId : prev)
      setVis(calcDesignVis())
      forceUpdate()   // ensure cache updates (units/levels/rooms) are reflected
    }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [])

  // Client Inspiration counts (hook must precede the early return below).
  const inspiration = useInspirationSummary(recordId, !sec.inspiration || vis === 'inspirationZoom')
  if (vis === 'hidden') return null

  /* ── Concept focus: show that concept's units (WorkFlowy model) ─────────── */
  if (vis === 'conceptZoom') {
    const conceptId  = (wf.zoomSection || '').split(':')[1] || ''
    const unassigned = ctx.unassignedRoomsCache?.[conceptId]
    if (unassigned === undefined) loadUnassignedRooms(conceptId)
    return (
      <div className="wf-list">
        <ControlBudgetSection conceptId={conceptId} />
        <ConceptUnitsSection conceptId={conceptId} />
        <ConceptDevMetricsSection conceptId={conceptId} />
        <ConceptFieldCategorySection conceptId={conceptId} suffix="demolition" />
        <ConceptFieldCategorySection conceptId={conceptId} suffix="grading" />
        <ConceptFieldCategorySection conceptId={conceptId} suffix="pools" />
        <ConceptFieldCategorySection conceptId={conceptId} suffix="sitescope" />
        {unassigned?.length > 0 && (
          <UnassignedSection conceptId={conceptId} rooms={unassigned} />
        )}
      </div>
    )
  }

  /* ── Unit focus: show that unit's levels ────────────────────────────────── */
  if (vis === 'unitZoom') {
    const parts     = (wf.zoomSection || '').split(':')
    const unitId    = parts[1] || ''
    const conceptId = parts[2] || ''
    return (
      <div className="wf-list">
        <ConceptLevelsSection unitId={unitId} conceptId={conceptId} />
      </div>
    )
  }

  /* ── Level focus: show that level's area fields + rooms ─────────────────── */
  if (vis === 'levelZoom') {
    const parts   = (wf.zoomSection || '').split(':')
    const levelId = parts[1] || ''
    const unitId  = parts[2] || ''
    if (unitId && ctx.levelCache?.[unitId] === undefined) loadLevels(unitId)
    const levelRec = ctx.levelCache?.[unitId]?.find(l => l.id === levelId) || null
    // Resolve conceptId from unitId (level-detail zoom keys don't carry it directly).
    let zoomConceptId = ''
    Object.keys(ctx.unitCache || {}).forEach(cid => {
      ;(ctx.unitCache[cid] || []).forEach(u => { if (u.id === unitId) zoomConceptId = cid })
    })
    return (
      <div className="wf-list">
        <ConceptRoomsSection levelId={levelId} conceptId={zoomConceptId} levelName={levelRec?.fields?.['Name'] || ''} />
        {levelRec && (
          <ConceptLevelAreaMetrics level={levelRec} onSave={(lid, fields) => saveLevelAreaFields(unitId, lid, fields)} />
        )}
      </div>
    )
  }

  /* ── Room focus: read-only field summary; Edit/Delete live on ZoomTitle's ⋯ menu ── */
  if (vis === 'roomZoom') {
    const parts   = (wf.zoomSection || '').split(':')
    const roomId  = parts[1] || ''
    const levelId = parts[2] || ''
    return <RoomDetailZoom roomId={roomId} levelId={levelId} />
  }

  /* ── BIM System / Element / Phase focus (bullet-zoom) — children-only, ZoomTitle owns the header ── */
  if (vis === 'bimEntityZoom') {
    const sec2 = wf.zoomSection || ''
    if (sec2.startsWith('bim-system-detail:')) {
      return <div className="wf-list"><BimSystemZoom systemId={sec2.slice('bim-system-detail:'.length)} /></div>
    }
    if (sec2.startsWith('bim-phase-detail:')) {
      const parts = sec2.split(':')
      return <div className="wf-list"><BimPhaseZoom elementId={parts[1] || ''} phase={parts[2] || ''} /></div>
    }
    return <div className="wf-list"><BimElementZoom elementId={sec2.slice('bim-element-detail:'.length)} /></div>
  }

  /* ── Category-wrapper zooms: children only, ZoomTitle supplies title/count/⋯ menu ── */
  if (vis === 'conceptUnitsZoom') {
    const conceptId = (wf.zoomSection || '').split(':')[2] || ''
    return <ConceptUnitsSection conceptId={conceptId} zoomMode />
  }
  if (vis === 'conceptMetricsZoom') {
    const conceptId = (wf.zoomSection || '').split(':')[2] || ''
    return <ConceptDevMetricsSection conceptId={conceptId} zoomMode />
  }
  if (vis === 'conceptCategoryZoom') {
    const parts = (wf.zoomSection || '').split(':')
    const conceptId = parts[2] || ''
    const suffix = parts[3] || ''
    return <ConceptFieldCategorySection conceptId={conceptId} suffix={suffix} zoomMode />
  }
  if (vis === 'unitLevelsZoom') {
    const parts   = (wf.zoomSection || '').split(':')
    const unitId  = parts[2] || ''
    let zoomConceptId = ''
    Object.keys(ctx.unitCache || {}).forEach(cid => {
      ;(ctx.unitCache[cid] || []).forEach(u => { if (u.id === unitId) zoomConceptId = cid })
    })
    return <ConceptLevelsSection unitId={unitId} conceptId={zoomConceptId} zoomMode />
  }
  if (vis === 'levelRoomsZoom') {
    const parts   = (wf.zoomSection || '').split(':')
    const levelId = parts[2] || ''
    let zoomConceptId = '', levelName = ''
    Object.keys(ctx.unitCache || {}).forEach(cid => {
      (ctx.unitCache[cid] || []).forEach(u => {
        (ctx.levelCache?.[u.id] || []).forEach(l => { if (l.id === levelId) { zoomConceptId = cid; levelName = l.fields['Name'] || '' } })
      })
    })
    return <ConceptRoomsSection levelId={levelId} conceptId={zoomConceptId} levelName={levelName} zoomMode />
  }

  /* Section children — shared by the Design root view, the home view, and each section's own
     children-only zoom. Every Design section is React-owned and zoomable; the engine cedes all
     sec:design:* to React (outline.js), so no design section falls back to the legacy renderer. */
  // One Owner Criteria record per project — the questionnaire the client completes during
  // onboarding. Renders as expandable categories via CriteriaCategoryRows, the same component
  // MilestoneSections' CriteriaSection uses under Pre-design Research / Conceptual Framework
  // milestones, so this branch expands/collapses identically in both places.
  const criteriaRecord = Array.isArray(criteria) && criteria.length ? criteria[0] : null
  const criteriaContent = (
    <>
      {!criteria && <div className="wf-loading">Loading criteria...</div>}
      {criteria && !criteriaRecord && (
        <div className="wf-loading" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>
          Criteria not collected yet — gathered when the client completes the onboarding questionnaire.
        </div>
      )}
      <CriteriaCategoryRows record={criteriaRecord} ownerKey="design" />
    </>
  )
  const parcelsContent = (
    <>
      {!parcels && <div className="wf-loading">Loading parcels...</div>}
      {parcels?.length === 0 && <div className="wf-loading" style={{ color: 'var(--aj-text-muted)' }}>No parcels</div>}
      {parcels?.map(rec => <ParcelRow key={rec.id} rec={rec} projectId={recordId} />)}
    </>
  )
  const conceptsContent = (
    <ConceptsList concepts={concepts} recordId={recordId} ownerKey="sec:design:concepts" />
  )
  const bimContent = (
    <>
      {!bim && <div className="wf-loading">Loading BIM systems...</div>}
      {bim?.length === 0 && <div className="wf-loading" style={{ color: 'var(--aj-text-muted)' }}>No BIM systems</div>}
      {bim?.map(b => (
        <BimRow key={b.id} item={b} projectId={recordId} projectName={(ctx.state?.milestones || [])[0]?.fields?.['Project Name'] || ''} />
      ))}
    </>
  )
  // Client Inspiration (docs/INSPIRATION_BOARD.md) — counts load lazily once the node is open.
  const inspirationContent = <InspirationRows projectId={recordId} summary={inspiration} />
  const inspirationMenu = menu => (
    <ActionMenuItem onClick={() => { menu.close(); openTeamInspirationBoard(recordId) }}>Open inspiration board</ActionMenuItem>
  )
  const inspirationSection = (
    <Section label="Client Inspiration" type="inspiration" count={inspiration ? inspiration.total : null}
      collapsed={sec.inspiration} onToggle={() => toggleSec('inspiration')} zoomKey="sec:design:inspiration"
      menuItems={inspirationMenu}>{inspirationContent}</Section>
  )
  // Per-section children-only zooms (section-zoom render rule: no header — ZoomTitle shows the name).
  if (vis === 'inspirationZoom') return <div className="wf-list">{inspirationContent}</div>
  if (vis === 'criteriaZoom') return <div className="wf-list">{criteriaContent}</div>
  if (vis === 'parcelsZoom')  return <div className="wf-list">{parcelsContent}</div>
  if (vis === 'conceptsZoom') return <div className="wf-list">{conceptsContent}</div>
  if (vis === 'bimZoom')      return <div className="wf-list">{bimContent}</div>

  // Design root zoom: show sections directly without the root node wrapper
  if (vis === 'designZoom') {
    return (
      <div className="wf-list">
        <Section label="Criteria" type="criteria" count={criteriaAnsweredTotal(criteriaRecord)}
          collapsed={sec.criteria} onToggle={() => toggleSec('criteria')} zoomKey="sec:design:criteria">{criteriaContent}</Section>
        <Section label="Parcels" type="parcels" count={parcels?.length ?? null}
          collapsed={sec.parcels} onToggle={() => toggleSec('parcels')} zoomKey="sec:design:parcels">{parcelsContent}</Section>
        <Section label="Concepts" type="concepts" count={concepts?.length ?? null}
          collapsed={sec.concepts} onToggle={() => toggleSec('concepts')} zoomKey="sec:design:concepts">{conceptsContent}</Section>
        <Section label="BIM Systems" type="bim" count={bim?.length ?? null}
          collapsed={sec.bim} onToggle={() => toggleSec('bim')} zoomKey="sec:design:bim">{bimContent}</Section>
        {inspirationSection}
        {/* Specifications branch moved to Production (Round 12.5) */}
      </div>
    )
  }

  // Home / referenceZoom: full root node with collapse toggle.
  // At project level it renders only when Reference is expanded (indented);
  // in Reference zoom it renders plain.
  if (vis === 'home' && wf.collapsed['root:reference'] !== false) return null
  return (
    <div className="wf-list">
      <div className={vis === 'referenceZoom' ? undefined : 'wf-ref-child'}>
      <div className={`wf-node wf-node--root wf-node--root-design${rootCollapsed ? ' wf-node--collapsed' : ''}`} data-key="root:design">
        <div className="wf-row">
          <button className="wf-triangle" onClick={() => { wf.collapsed['root:design'] = !rootCollapsed; saveViewState(); forceUpdate(); }}>
            <span className={`wf-tri-icon ${rootCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
          </button>
          <button
            className="wf-bullet wf-bullet--root"
            title="Focus on Design"
            onClick={() => ctx.zoomToRoot && ctx.zoomToRoot('design')}
          ></button>
          <div className="wf-text-wrap">
            <span className="wf-node-text wf-node-text--root">Design</span>
          </div>
          <button
            className="wf-menu-btn"
            data-fn="openMenu"
            data-type="root"
            data-key="root:design"
            data-rootname="design"
          >&#8943;</button>
        </div>

        {!rootCollapsed && (
          <div className="wf-children">
            <Section label="Criteria" type="criteria" count={criteriaAnsweredTotal(criteriaRecord)}
              collapsed={sec.criteria} onToggle={() => toggleSec('criteria')} zoomKey="sec:design:criteria">{criteriaContent}</Section>
            <Section label="Parcels" type="parcels" count={parcels?.length ?? null}
              collapsed={sec.parcels} onToggle={() => toggleSec('parcels')} zoomKey="sec:design:parcels">{parcelsContent}</Section>
            <Section label="Concepts" type="concepts" count={concepts?.length ?? null}
              collapsed={sec.concepts} onToggle={() => toggleSec('concepts')} zoomKey="sec:design:concepts">{conceptsContent}</Section>
            <Section label="BIM Systems" type="bim" count={bim?.length ?? null}
              collapsed={sec.bim} onToggle={() => toggleSec('bim')} zoomKey="sec:design:bim">{bimContent}</Section>
            {inspirationSection}
            {/* Specifications branch moved to Production (Round 12.5) */}
          </div>
        )}
      </div>
      </div>
    </div>
  )
}
